/**
 * プロジェクト内コンテンツ（イメージボード / 共有ノート / 共有ライブラリ）への
 * ディープリンクの生成・解析ユーティリティ。
 *
 * トークルーム / ショットコメント / DM などのメッセージに貼って「指定した
 * 場所」を示すために使う。URL はプロジェクト配下（`/{projectId}?tab=…`）を
 * 基本とし、アクセス時の権限確認は既存の仕組みに乗せる:
 * - プロジェクト非メンバー → BE の ProjectPermissionMixin が 403
 * - ノート / ライブラリの閲覧不可 → BE が存在秘匿の 404 → ページ内 EmptyState
 * - 未ログイン → RequireAuth が /signin?redirect=… へ
 *
 * URL スキーマ:
 * - イメージボード（アイテム指定）: 共有ノートのリンク + &item={itemId}
 * - イメージボード（位置指定）:     共有ノートのリンク + &bx={worldX}&by={worldY}&bs={scale}
 *   （ボードは共有ノートのページ。過去に発行された /{projectId}?tab=imageboard…
 *   リンクは ProjectWrapper がプロジェクトのメインのボードへ振り替える）
 * - 共有ノート:                     /{projectId}?tab=notes&note={noteId}
 *   （プロジェクト外は /notes/{noteId}。カードはノートに統合済みで、
 *   過去に発行された &card={cardId} / /notes?card= リンクも Notes 側で
 *   ノート ID として解決される）
 * - 共有ライブラリ:                 /{projectId}?tab=library&section={sectionId}&folder={folderId}&file={fileId}
 *   （プロジェクト外は /library?section=…&folder=…&file=…）
 * - ショットコメント:               /{projectId}/{shotId}?comment={commentId}
 *   （プレビューを開いて該当コメントへスクロール。ピン付きならピン先メディアを再生）
 *
 * コピーは Markdown リンク `[ラベル](URL)` 形式で行い、ラベルにノート名・
 * ファイル名等を埋め込む（貼り付け先で追加の API 参照を不要にする）。
 * MarkdownContent は parseInternalLink で同一オリジンの内部リンクを検出し、
 * 新規タブではなく SPA 内遷移（navigate）で開く。
 */

/** UUID の簡易判定（プロジェクト ID / 各リソース ID が該当する）。 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** 既知のディープリンク種別。メッセージ内のチップ表示（アイコン）に使う。 */
export type DeepLinkKind =
  | "imageboard"
  | "note"
  | "note-card"
  | "library"
  | "shot-comment";

/** 種別 → Material Symbols アイコン名（下部バーのノート / ライブラリと同じ視覚言語）。 */
export const DEEP_LINK_ICONS: Record<DeepLinkKind, string> = {
  imageboard: "imagesmode",
  note: "import_contacts",
  "note-card": "import_contacts",
  library: "home_storage",
  "shot-comment": "chat_bubble",
};

const projectTabUrl = (
  projectId: string,
  params: Record<string, string>,
): string => {
  const search = new URLSearchParams(params);
  return `${window.location.origin}/${projectId}?${search.toString()}`;
};

/**
 * 共有ノートの特定ページ（Markdown 1 枚 = Note）へのリンク。
 * カード（共有 TIPS）もノートに統合されたため同じ URL 形式を使う。
 */
export const buildNoteLink = (noteId: string, projectId?: string): string =>
  projectId
    ? projectTabUrl(projectId, { tab: "notes", note: noteId })
    : `${window.location.origin}/notes/${noteId}`;

const withParams = (url: string, params: Record<string, string>): string => {
  const next = new URL(url);
  Object.entries(params).forEach(([key, value]) => next.searchParams.set(key, value));
  return next.toString();
};

/** イメージボードの特定アイテム（画像）へのリンク。開くと対象へセンタリングする。 */
export const buildImageBoardItemLink = (
  boardId: string,
  itemId: string,
  projectId?: string,
): string => withParams(buildNoteLink(boardId, projectId), { item: itemId });

/**
 * イメージボードの任意の表示位置へのリンク。
 * bx / by はビューポート中心のワールド座標、bs はズーム倍率。
 */
export const buildImageBoardViewLink = (
  boardId: string,
  view: { x: number; y: number; scale: number },
  projectId?: string,
): string =>
  withParams(buildNoteLink(boardId, projectId), {
    bx: String(Math.round(view.x)),
    by: String(Math.round(view.y)),
    bs: String(Math.round(view.scale * 100) / 100),
  });

/** ボード内の位置を示すパラメータ（ノートのリンクに付くとイメージボードのリンク）。 */
export const BOARD_LINK_PARAMS = ["item", "bx", "by", "bs"] as const;
const isBoardLink = (params: URLSearchParams) =>
  BOARD_LINK_PARAMS.some((key) => params.has(key));

/**
 * 共有ライブラリの特定フォルダ / ファイルへのリンク。
 * folderId 省略時はセクション直下、fileId 指定時は開いた上で該当ファイルを
 * 選択（音源は頭出し）する。
 */
export const buildLibraryLink = (
  target: { sectionId: string; folderId?: string | null; fileId?: string | null },
  projectId?: string,
): string => {
  const params: Record<string, string> = { section: target.sectionId };
  if (target.folderId) params.folder = target.folderId;
  if (target.fileId) params.file = target.fileId;
  if (projectId) return projectTabUrl(projectId, { tab: "library", ...params });
  return `${window.location.origin}/library?${new URLSearchParams(params).toString()}`;
};

/**
 * ショットコメントへのリンク。プレビュー（`/{projectId}/{shotId}`）を開き、
 * 該当コメントまでスクロールする。ピン付きコメントならピン先メディアを再生する。
 */
export const buildShotCommentLink = (
  projectId: string,
  shotId: string,
  commentId: string,
): string =>
  `${window.location.origin}/${projectId}/${shotId}?comment=${commentId}`;

/** コピー用の Markdown リンク（`[ラベル](URL)`）。角括弧はエスケープする。 */
export const toMarkdownLink = (label: string, url: string): string =>
  `[${label.replace(/([[\]])/g, "\\$1")}](${url})`;

export interface InternalLink {
  /** SPA 内遷移先（origin を除いた pathname + search + hash）。 */
  to: string;
  /** 既知のディープリンク種別。該当しない内部リンクは null（遷移は可能）。 */
  kind: DeepLinkKind | null;
}

const detectKind = (url: URL): DeepLinkKind | null => {
  const segments = url.pathname.split("/").filter(Boolean);
  const params = url.searchParams;
  if (segments[0] === "notes") {
    if (isBoardLink(params)) return "imageboard";
    return params.get("card") ? "note-card" : "note";
  }
  if (segments[0] === "library") {
    return params.get("section") ? "library" : null;
  }
  if (segments.length === 1 && UUID_RE.test(segments[0])) {
    const tab = params.get("tab");
    if (tab === "imageboard") return "imageboard";
    if (tab === "notes") {
      if (isBoardLink(params)) return "imageboard";
      return params.get("card") ? "note-card" : "note";
    }
    if (tab === "library") return "library";
  }
  // プレビュー（/{projectId}/{shotId}）+ comment = ショットコメント
  if (
    segments.length === 2 &&
    UUID_RE.test(segments[0]) &&
    UUID_RE.test(segments[1]) &&
    params.get("comment")
  ) {
    return "shot-comment";
  }
  return null;
};

/**
 * href が同一オリジンの内部リンクなら SPA 内遷移情報を返す。
 * 外部 URL・ページ内 hash（`#ts-` タイムスタンプ等）・パース不能な値は null。
 */
export const parseInternalLink = (
  href: string | undefined | null,
): InternalLink | null => {
  if (!href || href.startsWith("#")) return null;
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return null;
  }
  if (!/^https?:$/.test(url.protocol)) return null;
  if (url.origin !== window.location.origin) return null;
  return {
    to: `${url.pathname}${url.search}${url.hash}`,
    kind: detectKind(url),
  };
};
